import { createContext, useContext, useState } from 'react';
import { LoadingOverlay } from '../components/ui/spinner';

const LoadingContext = createContext({});

export function LoadingProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Carregando...');

  /**
   * Mostra o loading com texto customizado
   */
  const showLoading = (text = 'Carregando...') => {
    setLoadingText(text);
    setLoading(true);
  };

  /**
   * Esconde o loading
   */
  const hideLoading = () => {
    setLoading(false);
  };

  /**
   * Executa uma função async com loading automático
   * Exemplo: await withLoading(async () => { ... }, 'Salvando dados...')
   */
  const withLoading = async (fn, text = 'Carregando...') => {
    try {
      showLoading(text);
      const result = await fn();
      return result;
    } finally {
      hideLoading();
    }
  };

  return (
    <LoadingContext.Provider
      value={{
        loading,
        showLoading,
        hideLoading,
        withLoading
      }}
    >
      {children}
      <LoadingOverlay show={loading} text={loadingText} />
    </LoadingContext.Provider>
  );
}

/**
 * Hook para usar o loading em qualquer componente
 *
 * Exemplo de uso:
 * ```js
 * const { showLoading, hideLoading, withLoading } = useLoading();
 *
 * // Opção 1: Manual
 * showLoading('Carregando dados...');
 * await fetchData();
 * hideLoading();
 *
 * // Opção 2: Automático
 * await withLoading(async () => {
 *   await fetchData();
 * }, 'Carregando dados...');
 * ```
 */
export function useLoading() {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within LoadingProvider');
  }
  return context;
}

export default LoadingProvider;
